import React from "react";
import { Helmet } from "react-helmet";
import { FileText, Shield, AlertTriangle, Globe, Cookie, CreditCard, Phone, Lock, Scale, Award } from "lucide-react";

const Navigation = () => {
  return (
    <nav className="dark-header">
      <a href="/">
        <img src="/logos/logo-v5.jpg" alt="ARCANA" className="dark-logo-nav" />
      </a>
      <div className="dark-nav">
        <a href="/" className="dark-nav-link">HOME</a>
        <a href="/#about" className="dark-nav-link">ABOUT US</a>
        <a href="/services" className="dark-nav-link">SERVICES</a>
        <a href="/#pricing" className="dark-nav-link">PRICING</a>
        <a href="/faq" className="dark-nav-link">FAQ</a>
        <a href="/#assessment" className="dark-nav-link">CONTACT</a>
        <button className="btn-primary" onClick={() => window.location.href = '/#assessment'}>
          FREE ASSESSMENT
        </button>
      </div>
      <button className="mobile-menu-btn">
        <span style={{color: 'var(--oro-arcana)'}}>☰</span>
      </button>
    </nav>
  );
};

const LegalPage = () => {
  return (
    <div className="App">
      <Helmet>
        <title>Legal Information, Terms & Privacy Policy | ARCANA CRYPTO LTD</title>
        <meta name="description" content="Terms of service, privacy policy, GDPR rights, cookie policy, refund policy, and complaints procedure for ARCANA CRYPTO LTD. Company No. 16371124. Hiscox PI insured £1M." />
        <link rel="canonical" href="https://arcana-crypto.com/legal.html" />
      </Helmet>
      <Navigation />
      
      <section className="section" style={{marginTop: '128px', minHeight: '100vh'}}>
        <div className="section-container">
          <h1 className="section-title" data-testid="legal-page-title">Legal Information</h1>
          <p className="section-subtitle">Terms of Service, Privacy Policy, GDPR Compliance & Complaints Procedure</p>

          {/* Trust Badges */}
          <div style={{display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '24px', marginBottom: '48px'}}>
            <span style={{background: 'var(--nero-profondo)', border: '1px solid var(--oro-arcana)', padding: '8px 16px', fontSize: '13px', color: 'var(--oro-arcana)', fontWeight: '600'}}>Company No. 16371124</span>
            <span style={{background: 'var(--nero-profondo)', border: '1px solid var(--oro-arcana)', padding: '8px 16px', fontSize: '13px', color: 'var(--oro-arcana)', fontWeight: '600'}}>PI Insured £1M — Hiscox</span>
            <span style={{background: 'var(--nero-profondo)', border: '1px solid var(--oro-arcana)', padding: '8px 16px', fontSize: '13px', color: 'var(--oro-arcana)', fontWeight: '600'}}>Cyber Insured £100K — Hiscox</span>
            <span style={{background: 'var(--nero-profondo)', border: '1px solid var(--oro-arcana)', padding: '8px 16px', fontSize: '13px', color: 'var(--oro-arcana)', fontWeight: '600'}}>Non-Custodial</span>
          </div>

          <div style={{maxWidth: '1000px', margin: '0 auto'}}>
            
            {/* Table of Contents */}
            <div style={{background: 'var(--nero-grigio)', border: '1px solid var(--border-subtle)', padding: '28px', marginBottom: '48px'}}>
              <h3 style={{color: 'var(--oro-arcana)', fontSize: '18px', marginBottom: '16px'}}>Contents</h3>
              <ol style={{marginLeft: '20px'}}>
                {['Company Information', 'Terms of Service', 'Privacy Policy & GDPR', 'Cookie Policy', 'Refund Policy', 'Important Disclaimers', 'Governing Law', 'International Client Notice', 'Proprietary Software & Intellectual Property', 'Insurance Information', 'Complaints Procedure'].map((item, i) => (
                  <li key={i} style={{marginBottom: '8px'}}>
                    <a href={`#section-${i+1}`} style={{color: 'var(--grigio-chiaro)', textDecoration: 'none', fontSize: '15px'}}>{item}</a>
                  </li>
                ))}
              </ol>
            </div>

            {/* 1. Company Information */}
            <div className="legal-section" id="section-1">
              <h2 className="legal-title">
                <FileText size={28} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
                1. Company Information
              </h2>
              <div className="legal-content">
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', margin: '16px 0 24px'}}>
                  {[
                    {label: 'Legal Name', value: 'ARCANA CRYPTO LTD'},
                    {label: 'Company Number', value: '16371124'},
                    {label: 'Registered', value: 'England and Wales'},
                    {label: 'Founded', value: 'April 2025'},
                    {label: 'Business Type', value: 'Professional Technical Services'},
                    {label: 'Contact', value: 'legal@arcana-crypto.com'},
                    {label: 'Professional Indemnity', value: '£1,000,000 — Hiscox'},
                    {label: 'Cyber Insurance', value: '£100,000 — Hiscox'},
                  ].map((item, i) => (
                    <div key={i} style={{background: 'var(--nero-grigio)', padding: '16px', borderLeft: '3px solid var(--oro-arcana)'}}>
                      <div style={{fontSize: '13px', color: 'var(--grigio-medio)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px'}}>{item.label}</div>
                      <div style={{fontSize: '16px', color: '#fff', fontWeight: '600'}}>{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 2. Terms of Service */}
            <div className="legal-section" id="section-2">
              <h2 className="legal-title">
                <Shield size={28} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
                2. Terms of Service
              </h2>
              <div className="legal-content">
                <h3 className="legal-subtitle">2.1 Service Scope</h3>
                <p>Arcana Crypto LTD provides technical services only for cryptocurrency wallet recovery, security analysis, and forensic investigation. We do not provide:</p>
                <ul className="column-list">
                  <li>Custody or management of client funds</li>
                  <li>Financial, investment, or tax advice</li>
                  <li>Legal counsel or representation</li>
                  <li>Guaranteed recovery outcomes</li>
                  <li>Exchange, brokerage, or trading services</li>
                </ul>

                <h3 className="legal-subtitle">2.2 Client Obligations</h3>
                <p>By engaging our services, clients agree to:</p>
                <ul className="column-list">
                  <li>Be the rightful owner or legally authorised representative of the digital assets in question</li>
                  <li>Provide accurate and complete information relevant to the case</li>
                  <li>Comply with all applicable laws and regulations in their jurisdiction</li>
                  <li>Understand that recovery is probabilistic, not guaranteed</li>
                  <li>Not use our services for any unlawful purpose</li>
                </ul>

                <h3 className="legal-subtitle">2.3 Non-Custodial Operations</h3>
                <p>
                  Arcana Crypto operates strictly on a <strong>non-custodial basis</strong>. We never take possession, 
                  custody, or control of client funds or assets. All recovered credentials are delivered 
                  securely to the client via encrypted channels. The client retains full control of their assets at all times.
                </p>

                <h3 className="legal-subtitle">2.4 Pricing & Payment</h3>
                <p>
                  Published prices are standard reference rates. Final pricing is determined based on 
                  case complexity, computational requirements, and risk assessment. All pricing is 
                  confirmed and agreed <strong>in writing before work commences</strong>. No work begins without explicit client authorisation.
                </p>

                <h3 className="legal-subtitle">2.5 Limitation of Liability</h3>
                <p>Arcana Crypto's total liability is limited to the fees paid for services rendered. We are not responsible for:</p>
                <ul className="column-list">
                  <li>Lost opportunity costs or market fluctuations during the recovery period</li>
                  <li>Unsuccessful recovery attempts where the probabilistic nature was disclosed</li>
                  <li>Third-party actions, blockchain protocol changes, or network events</li>
                  <li>Client errors in providing information or following instructions</li>
                  <li>Consequential, incidental, or indirect damages of any kind</li>
                </ul>

                <h3 className="legal-subtitle">2.6 Service Termination</h3>
                <p>
                  Either party may terminate the engagement at any time by providing written notice. Upon termination, the client shall pay for all services rendered up to the date of termination. All client data will be handled in accordance with our Privacy Policy.
                </p>
              </div>
            </div>

            {/* 3. Privacy Policy & GDPR */}
            <div className="legal-section" id="section-3">
              <h2 className="legal-title">
                <Lock size={28} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
                3. Privacy Policy & GDPR Compliance
              </h2>
              <div className="legal-content">
                <h3 className="legal-subtitle">3.1 Data Controller</h3>
                <p><strong>ARCANA CRYPTO LTD</strong> (Company No. 16371124) is the data controller for all personal data processed in connection with our services. For all data-related enquiries, contact: <strong>legal@arcana-crypto.com</strong></p>

                <h3 className="legal-subtitle">3.2 Data We Collect</h3>
                <p>We collect only the minimum data necessary to provide our services:</p>
                <ul className="column-list">
                  <li><strong>Contact information:</strong> Email address, name (if voluntarily provided)</li>
                  <li><strong>Case-related technical data:</strong> Wallet type, cryptocurrency, partial seed information, encryption method</li>
                  <li><strong>Temporary access credentials:</strong> Only when absolutely necessary for recovery, with explicit client consent</li>
                  <li><strong>Communication records:</strong> Emails and messages related to service delivery</li>
                </ul>

                <h3 className="legal-subtitle">3.3 Legal Basis for Processing</h3>
                <p>We process personal data under the following legal bases (UK GDPR Article 6):</p>
                <ul className="column-list">
                  <li><strong>Contract performance:</strong> Processing necessary to deliver the services you requested</li>
                  <li><strong>Legitimate interests:</strong> Business administration, fraud prevention, service improvement</li>
                  <li><strong>Consent:</strong> Where explicitly given by the client (e.g., marketing communications)</li>
                  <li><strong>Legal obligation:</strong> Where required by law or regulation</li>
                </ul>

                <h3 className="legal-subtitle">3.4 Data Protection Measures</h3>
                <ul className="column-list">
                  <li>All communications encrypted via ProtonMail and Signal</li>
                  <li>Recovery operations performed locally and offline — no cloud processing</li>
                  <li>Sensitive case data deleted upon service completion (unless retention is requested)</li>
                  <li>No sharing with third parties except where legally required</li>
                  <li>Non-Disclosure Agreement (NDA) available upon request</li>
                </ul>

                <h3 className="legal-subtitle">3.5 Data Retention</h3>
                <ul className="column-list">
                  <li><strong>Active case data:</strong> Retained for the duration of the engagement plus 30 days</li>
                  <li><strong>Contact information:</strong> Retained for up to 12 months after last contact for follow-up purposes</li>
                  <li><strong>Financial records:</strong> Retained for 6 years as required by UK law (HMRC)</li>
                  <li><strong>Sensitive credentials:</strong> Deleted immediately upon successful delivery to client</li>
                </ul>

                <h3 className="legal-subtitle">3.6 Your Rights Under UK GDPR</h3>
                <p>You have the following rights regarding your personal data:</p>
                <ul className="column-list">
                  <li><strong>Right of access:</strong> Request a copy of the personal data we hold about you</li>
                  <li><strong>Right to rectification:</strong> Request correction of inaccurate or incomplete data</li>
                  <li><strong>Right to erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
                  <li><strong>Right to data portability:</strong> Request transfer of your data in a machine-readable format</li>
                  <li><strong>Right to object:</strong> Object to processing based on legitimate interests</li>
                  <li><strong>Right to restrict processing:</strong> Request limitation of processing in certain circumstances</li>
                  <li><strong>Right to withdraw consent:</strong> Withdraw consent at any time where processing is consent-based</li>
                </ul>
                <p>To exercise any of these rights, contact: <strong>legal@arcana-crypto.com</strong>. We will respond within 30 days.</p>

                <h3 className="legal-subtitle">3.7 International Transfers</h3>
                <p>We do not routinely transfer personal data outside the United Kingdom. If a transfer is necessary, appropriate safeguards will be implemented in compliance with UK GDPR requirements.</p>
              </div>
            </div>

            {/* 4. Cookie Policy */}
            <div className="legal-section" id="section-4">
              <h2 className="legal-title">
                <Cookie size={28} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
                4. Cookie Policy
              </h2>
              <div className="legal-content">
                <p>This website uses a limited number of cookies to ensure functionality and understand site usage. We do not use advertising or marketing cookies.</p>
                <div style={{overflowX: 'auto'}}>
                  <table style={{width: '100%', borderCollapse: 'collapse', margin: '20px 0'}}>
                    <thead>
                      <tr>
                        <th style={{background: 'var(--nero-grigio)', color: 'var(--oro-arcana)', padding: '14px', textAlign: 'left', borderBottom: '2px solid var(--oro-arcana)', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px'}}>Cookie / Service</th>
                        <th style={{background: 'var(--nero-grigio)', color: 'var(--oro-arcana)', padding: '14px', textAlign: 'left', borderBottom: '2px solid var(--oro-arcana)', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px'}}>Type</th>
                        <th style={{background: 'var(--nero-grigio)', color: 'var(--oro-arcana)', padding: '14px', textAlign: 'left', borderBottom: '2px solid var(--oro-arcana)', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px'}}>Purpose</th>
                        <th style={{background: 'var(--nero-grigio)', color: 'var(--oro-arcana)', padding: '14px', textAlign: 'left', borderBottom: '2px solid var(--oro-arcana)', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px'}}>Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        {name: 'Google Analytics', type: 'Analytics', purpose: 'Anonymous site usage statistics. No personally identifiable information collected.', duration: 'Up to 2 years'},
                        {name: 'Cloudflare (__cf_bm)', type: 'Strictly necessary', purpose: 'Bot management and DDoS protection.', duration: '30 minutes'},
                        {name: 'Spline 3D', type: 'Functional', purpose: 'Renders 3D animations. No tracking or personal data.', duration: 'Session only'},
                      ].map((row, i) => (
                        <tr key={i}>
                          <td style={{padding: '12px 14px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--grigio-chiaro)', fontSize: '15px'}}>{row.name}</td>
                          <td style={{padding: '12px 14px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--grigio-chiaro)', fontSize: '15px'}}>{row.type}</td>
                          <td style={{padding: '12px 14px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--grigio-chiaro)', fontSize: '15px'}}>{row.purpose}</td>
                          <td style={{padding: '12px 14px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--grigio-chiaro)', fontSize: '15px'}}>{row.duration}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p>You can control cookies through your browser settings. Disabling cookies may affect some site functionality but will not prevent you from contacting us.</p>
              </div>
            </div>

            {/* 5. Refund Policy */}
            <div className="legal-section" id="section-5">
              <h2 className="legal-title">
                <CreditCard size={28} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
                5. Refund Policy
              </h2>
              <div className="legal-content">
                <h3 className="legal-subtitle">5.1 Free Assessment</h3>
                <p>Initial case assessments are provided at <strong>no cost and no obligation</strong>. No payment is required at any point during the assessment phase.</p>

                <h3 className="legal-subtitle">5.2 Agreed Services</h3>
                <p>Once a service agreement is confirmed in writing and work has commenced:</p>
                <ul className="column-list">
                  <li>Fees for <strong>completed work</strong> are non-refundable</li>
                  <li>If recovery is unsuccessful, any <strong>success-based fee component</strong> is not charged</li>
                  <li>If work has not yet begun, a <strong>full refund</strong> will be issued upon request</li>
                  <li>Partial refunds may be issued at our discretion for partially completed work</li>
                </ul>

                <h3 className="legal-subtitle">5.3 Dispute Resolution</h3>
                <p>If you are dissatisfied with the outcome, we encourage you to use our Complaints Procedure (Section 11) before seeking external resolution.</p>
              </div>
            </div>

            {/* 6. Important Disclaimers */}
            <div className="legal-section" id="section-6">
              <h2 className="legal-title">
                <AlertTriangle size={28} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
                6. Important Disclaimers
              </h2>
              <div className="legal-content">
                {[
                  {title: 'NO CUSTODY', text: 'Arcana Crypto never takes custody, possession, or control of client funds or digital assets. All operations are strictly non-custodial.'},
                  {title: 'NO GUARANTEES', text: 'Recovery success depends on cryptographic and technical factors specific to each case. We provide honest probability assessments, not guarantees.'},
                  {title: 'NO FINANCIAL ADVICE', text: 'We provide technical services only. No investment, financial, tax, or legal advice is offered, implied, or should be inferred.'},
                  {title: 'CLIENT RESPONSIBILITY', text: 'Clients are responsible for verifying their legal ownership, complying with applicable laws, and understanding the probabilistic nature of recovery.'},
                  {title: 'OWNER-AUTHORISED ONLY', text: 'We only perform recovery for the rightful owner or their legally authorised representative. We reserve the right to refuse any engagement where ownership cannot be established.'},
                ].map((item, i) => (
                  <div key={i} className="disclaimer-box">
                    <p><strong>{item.title}:</strong> {item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* 7. Governing Law */}
            <div className="legal-section" id="section-7">
              <h2 className="legal-title">
                <Scale size={28} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
                7. Governing Law
              </h2>
              <div className="legal-content">
                <p>These terms are governed by and construed in accordance with the <strong>laws of England and Wales</strong>. Any disputes arising from or in connection with these terms or our services shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
              </div>
            </div>

            {/* 8. International Client Notice */}
            <div className="legal-section" id="section-8" style={{background: 'var(--nero-profondo)', border: '2px solid var(--oro-arcana)', padding: '32px'}}>
              <h2 className="legal-title">
                <Globe size={28} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
                8. International Client Notice
              </h2>
              <div className="legal-content">
                <p>Arcana Crypto LTD is a company registered in England and Wales and operates exclusively under the laws of England and Wales.</p>
                <p>The services provided are <strong>strictly technical in nature</strong> and do not constitute financial services, investment advice, legal counsel, or custodial activity under any jurisdiction.</p>
                <p>Arcana Crypto does not act as a Virtual Asset Service Provider (VASP), exchange, broker, custodian, or financial intermediary as defined by any regulatory framework including but not limited to UK FCA, EU MiCA, or US SEC/FinCEN regulations.</p>
                <p>Clients outside the United Kingdom are solely responsible for ensuring that the use of our services complies with the laws, regulations, and sanctions applicable in their jurisdiction.</p>
                <p style={{fontWeight: '600', color: 'var(--oro-arcana)', marginTop: '20px'}}>
                  By engaging our services, the client acknowledges and accepts these conditions.
                </p>
              </div>
            </div>

            {/* 9. Proprietary Software & IP */}
            <div className="legal-section" id="section-9">
              <h2 className="legal-title">9. Proprietary Software & Intellectual Property</h2>
              <div className="legal-content">
                <p>All recovery tools, methodologies, algorithms, and software used by Arcana Crypto are <strong>proprietary and protected by intellectual property rights</strong>.</p>
                <p>Clients receive recovery services only. No software licences, source code, technical methodologies, or proprietary processes are transferred, disclosed, or made available to the client or any third party.</p>
                <p>All documentation provided (including forensic reports) is for the client's use in connection with the specific engagement and may not be reproduced, distributed, or used for commercial purposes without written consent.</p>
              </div>
            </div>

            {/* 10. Insurance Information */}
            <div className="legal-section" id="section-10">
              <h2 className="legal-title">
                <Award size={28} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
                10. Insurance Information
              </h2>
              <div className="legal-content">
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', margin: '16px 0 24px'}}>
                  {[
                    {label: 'Professional Indemnity', value: '£1,000,000'},
                    {label: 'Cyber Insurance', value: '£100,000'},
                    {label: 'Insurer', value: 'Hiscox'},
                    {label: 'Status', value: 'Active'},
                  ].map((item, i) => (
                    <div key={i} style={{background: 'var(--nero-grigio)', padding: '16px', borderLeft: '3px solid var(--oro-arcana)'}}>
                      <div style={{fontSize: '13px', color: 'var(--grigio-medio)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px'}}>{item.label}</div>
                      <div style={{fontSize: '16px', color: '#fff', fontWeight: '600'}}>{item.value}</div>
                    </div>
                  ))}
                </div>
                <p>Our Professional Indemnity insurance covers claims arising from professional negligence, errors, or omissions. Our Cyber Insurance provides additional protection for data-related incidents. Proof of insurance is available upon request.</p>
              </div>
            </div>

            {/* 11. Complaints Procedure */}
            <div className="legal-section" id="section-11">
              <h2 className="legal-title">
                <Phone size={28} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
                11. Complaints Procedure
              </h2>
              <div className="legal-content">
                <p>Arcana Crypto is committed to providing a high standard of service. If you are dissatisfied with any aspect of our service, we encourage you to follow our formal complaints procedure.</p>

                {[
                  {title: 'Submit Your Complaint', text: 'Send a detailed written complaint to: legal@arcana-crypto.com. Please include: your name (or case reference), a clear description of the issue, the outcome you are seeking, and any supporting documentation.'},
                  {title: 'Acknowledgement', text: 'We will acknowledge receipt of your complaint within 3 working days and assign a dedicated team member to review your case.'},
                  {title: 'Investigation & Response', text: 'We aim to provide a full written response within 14 working days of receiving your complaint. If the matter requires additional time, we will notify you of the expected timeline.'},
                  {title: 'Escalation', text: 'If you are not satisfied with our response, you may request an escalation to senior management by replying to the response email. The escalation review will be completed within 14 additional working days.'},
                ].map((step, i) => (
                  <div key={i} style={{position: 'relative', paddingLeft: '48px', marginBottom: '24px'}}>
                    <div style={{position: 'absolute', left: 0, top: 0, width: '32px', height: '32px', background: 'var(--oro-arcana)', color: '#000', fontWeight: '700', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>{i + 1}</div>
                    <h3 className="legal-subtitle" style={{marginTop: 0}}>{step.title}</h3>
                    <p>{step.text}</p>
                  </div>
                ))}

                <div style={{background: 'var(--nero-profondo)', border: '2px solid var(--oro-arcana)', padding: '28px', marginTop: '24px', textAlign: 'center'}}>
                  <p><strong style={{color: 'var(--oro-arcana)'}}>External Resolution:</strong> If the matter remains unresolved after exhausting our internal complaints procedure, you may seek resolution through the courts of England and Wales or an appropriate alternative dispute resolution (ADR) service.</p>
                </div>
              </div>
            </div>

            {/* Last Updated */}
            <div style={{textAlign: 'center', marginTop: '80px', padding: '32px', background: 'var(--nero-grigio)', border: '1px solid var(--border-subtle)'}}>
              <p style={{color: 'var(--grigio-medio)', fontSize: '14px'}}>
                <strong>Last Updated:</strong> February 2026
              </p>
              <p style={{color: 'var(--grigio-medio)', fontSize: '14px', marginTop: '8px'}}>
                For questions regarding these terms, contact: <a href="mailto:legal@arcana-crypto.com" style={{color: 'var(--oro-arcana)'}}>legal@arcana-crypto.com</a>
              </p>
              <p style={{color: 'var(--grigio-medio)', fontSize: '14px', marginTop: '8px'}}>
                ARCANA CRYPTO LTD — Company No. 16371124 — Registered in England and Wales
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-bottom">
            <p className="footer-copyright">
              © 2025-2026 ARCANA CRYPTO LTD - All Rights Reserved
            </p>
            <p style={{fontSize: '14px', color: 'var(--oro-arcana)', marginTop: '8px'}}>
              PI Insured £1M | Cyber Insured £100K — Hiscox
            </p>
            <p style={{fontSize: '13px', color: 'var(--grigio-medio)', marginTop: '12px'}}>
              <a href="mailto:legal@arcana-crypto.com" className="footer-email">
                legal@arcana-crypto.com
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LegalPage;
